import { ModelDao, ModelDocumentCandidateDao, ModelDocumentDao } from '@rock/db-admin';
import {
    assertCanReplace,
    assertValidCoverageRule,
    assertValidDocumentScope,
    documentReachKey,
    replacementChain,
} from '@rock/manuals';
import {
    type DocumentCoverage,
    type ModelDocTag,
    type ModelDocument,
    type ModelDocumentCandidate,
    type ModelDocumentId,
    type ModelId,
    TimestampSchema,
} from '@rock/types';
import type { CoverageDraft, DocumentScope } from './coverageDraft';

const modelDao = new ModelDao();
const modelDocumentDao = new ModelDocumentDao();
const candidateDao = new ModelDocumentCandidateDao();

/** One save of "what is this document, what is it for, and which models does its rule reach". */
export interface DocumentEdit {
    tags: ModelDocTag[];
    title: string | undefined;
    scope: DocumentScope;
    /** `null` removes the rule. */
    coverage: CoverageDraft | null;
}

/**
 * Save an edit to a published document from any admin page. Tags and title are one field patch; the reach
 * (brands, types, rule) another, written only when it changed — so a plain re-tag never wakes the
 * `syncDocumentCoverage` sweep. Returns the document as saved.
 */
export async function saveDocumentEdit(doc: ModelDocument, edit: DocumentEdit): Promise<ModelDocument> {
    const now = new Date().toISOString();
    assertValidDocumentScope(edit.scope);
    const coverage = edit.coverage ? buildCoverageRule(edit.coverage, now) : undefined;
    const next: ModelDocument = { ...doc, tags: edit.tags, title: edit.title, ...edit.scope, coverage };
    await modelDocumentDao.setDocumentTags(doc.id, edit.tags, edit.title);
    if (scopeChanged(doc, edit.scope) || documentReachKey(doc) !== documentReachKey(next)) {
        await modelDocumentDao.setDocumentReach(doc.id, { ...edit.scope, coverage: coverage ?? null });
    }
    return next;
}

/** The stored rule for a confirmed draft — predicates validated here too: write-time, not just UI. */
export function buildCoverageRule(draft: CoverageDraft, now: string): DocumentCoverage {
    assertValidCoverageRule(draft);
    return {
        prefixes: draft.prefixes,
        ...(draft.ranges.length > 0 ? { ranges: draft.ranges } : {}),
        confirmedAt: TimestampSchema.parse(now),
    };
}

/**
 * Take a document off one model: the link goes with the marks it held over the model's other documents,
 * its backing candidate (if any) returns to review — or is vetoed — and the artifact itself goes when no
 * model links it any more (the mirror trigger then removes its public bytes). The staged candidate stays
 * re-publishable either way.
 */
export async function unlinkDocumentFromModel(
    modelId: ModelId,
    doc: ModelDocument,
    backing: ModelDocumentCandidate | undefined,
    alsoNotRelevant: boolean,
): Promise<void> {
    await modelDao.unlinkDocumentAndUnstrand(modelId, doc.id);
    // Only candidates have dispositions; a migrated or pre-offer uploaded doc may have none.
    if (backing) await candidateDao.setDisposition(modelId, doc.id, alsoNotRelevant ? 'not-relevant' : undefined);
    const remaining = await modelDao.findModelsLinkingDocument(doc.id);
    if (remaining.length === 0) await modelDocumentDao.deleteModelDocument(doc.id);
}

/** The tags a publish leaves on a document: the form's, plus any the same bytes already carry (`withMergedTags`). */
export function publishedTags(tags: ModelDocTag[], existing: Pick<ModelDocument, 'tags'> | undefined): ModelDocTag[] {
    return [...new Set([...(existing?.tags ?? []), ...tags])];
}

/**
 * Mark `doc` as replaced by a newer revision. The server then supersedes it on every model that links
 * both, and leaves it current where only it reaches. Refuses a loop through the revision's own chain, and a
 * revision without `doc`'s lead kind.
 */
export async function replaceDocument(doc: ModelDocument, successorId: ModelDocumentId): Promise<void> {
    const chain = await revisionChain(successorId);
    const successor = chain.get(successorId);
    if (!successor) throw new Error('The newer revision is no longer published');
    assertCanReplace(doc, successor, chain);
    await modelDocumentDao.setDocumentReplacedBy(doc.id, successorId);
}

/** Undo a replacement: the server clears the marks it made, and the ordinary ranking decides again. */
export async function undoReplacement(doc: ModelDocument): Promise<void> {
    await modelDocumentDao.setDocumentReplacedBy(doc.id, null);
}

/**
 * The newest still-published revision in `docId`'s chain (the document itself when nothing replaced it):
 * the chain is read in order and stops at a revision that was since unpublished.
 */
export async function newestRevision(docId: ModelDocumentId): Promise<ModelDocument | undefined> {
    const docs = await revisionChain(docId);
    return replacementChain(docId, docs)
        .flatMap((id) => docs.get(id) ?? [])
        .at(-1);
}

/** Whether two rules say the same thing (`confirmedAt` excluded — a re-save isn't a new confirmation). */
export function sameCoverageRule(a: DocumentCoverage | undefined, b: DocumentCoverage | undefined): boolean {
    const scope = { brandIds: [], typeIds: [] };
    return documentReachKey({ ...scope, coverage: a }) === documentReachKey({ ...scope, coverage: b });
}

function scopeChanged(doc: ModelDocument, scope: DocumentScope): boolean {
    const key = (ids: readonly string[]) => [...ids].sort().join(',');
    return key(doc.brandIds) !== key(scope.brandIds) || key(doc.typeIds) !== key(scope.typeIds);
}

/** A document and the revisions after it. */
async function revisionChain(docId: ModelDocumentId): Promise<Map<ModelDocumentId, ModelDocument>> {
    const doc = await modelDocumentDao.getModelDocument(docId);
    return doc ? modelDocumentDao.loadReplacementChains([doc]) : new Map();
}
